import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Layout from './Layout';
import {
  demanderSupport, lireAssistance, listerMesDemandes,
} from './api';

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : '');

const SUJETS = [
  { code: 'ASSURANCE', libelle: "Une question d'assurance" },
  { code: 'GENERAL', libelle: "L'utilisation du site" },
  { code: 'TECHNIQUE', libelle: 'Une difficulté technique' },
];

const VIDE = {
  type: 'QUESTION', sujet: 'ASSURANCE', message: '', telephone: '', moment: '',
};

/**
 * L'aide : poser une question, ou demander un accompagnement.
 *
 * Sur l'accompagnement, on n'annonce **que ce qui est vérifiable** — ce que
 * le contrat de l'assuré rembourse. Le coût d'un expert dépend de l'ampleur
 * du dossier : un montant lancé avant de l'avoir vu serait tenu pour un
 * devis, et il serait faux.
 */
export default function Aide({ expertiseId, onRetour, onEtape, intention }) {
  const [demandes, setDemandes] = useState([]);
  const [assistance, setAssistance] = useState(null);
  const [formulaire, setFormulaire] = useState(null);
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);

  const recharger = React.useCallback(() => {
    listerMesDemandes().then(setDemandes).catch(() => {});
  }, []);

  useEffect(() => { recharger(); }, [recharger]);

  // Arrivé par « aidez-moi » : la demande s'ouvre sans un clic de plus.
  useEffect(() => {
    if (intention) setFormulaire({ ...VIDE, type: intention,
                                   sujet: 'ASSURANCE' });
  }, [intention]);

  useEffect(() => {
    if (!expertiseId) return;
    lireAssistance(expertiseId).then(setAssistance).catch(() => {});
  }, [expertiseId]);

  const envoyer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      await demanderSupport({
        ...formulaire,
        expertise: expertiseId || null,
      });
      setFormulaire(null);
      setMessage(formulaire.type === 'RAPPEL'
        ? 'Votre demande de rappel est enregistrée. Nous vous appelons dès que possible.'
        : 'Votre message est parti. Nous vous répondons dès que possible.');
      recharger();
    } catch {
      setErreur("L'envoi n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  const ouvrir = (type, sujet) =>
    setFormulaire({ ...VIDE, type, sujet: sujet || VIDE.sujet });

  const couverture = assistance?.couverture;

  return (
    <Layout
      titre="Besoin d'aide ?"
      introduction="Une question sur votre dossier, sur votre contrat, ou sur le site. Et si c'est trop compliqué, vous pouvez être accompagné."
      onEtape={onEtape}
      actions={onRetour && <Button onClick={onRetour}>Retour</Button>}
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
      {message && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h3" sx={{ mb: 1 }}>J'ai une question</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sur votre sinistre, votre contrat, ou la façon de vous servir du
            site. Nous sommes une petite équipe : la réponse peut prendre un
            jour ou deux, mais elle viendra.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={() => ouvrir('QUESTION')}>
              Écrire un message
            </Button>
            <Button variant="outlined" onClick={() => ouvrir('RAPPEL')}>
              Demander à être rappelé
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            C'est trop compliqué, je veux être accompagné
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Un expert d'assuré défend vos intérêts face à la compagnie :
            il chiffre, argumente et négocie à votre place.
          </Typography>

          {couverture?.connue ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Votre contrat prévoit la prise en charge de ces honoraires —
                {' '}{couverture.libelle.toLowerCase()}.
              </Typography>
              {couverture.bareme && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {couverture.bareme}
                </Typography>
              )}
              {couverture.source && (
                <Typography variant="caption" color="text.secondary"
                            component="div" sx={{ mt: 0.5 }}>
                  {couverture.source}
                </Typography>
              )}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              La plupart des contrats prennent ces honoraires en charge, en
              tout ou en partie. Nous n'avons pas encore relevé ce que
              prévoient vos conditions générales — nous le vérifierons avec
              vous.
            </Alert>
          )}

          <Typography variant="body2" sx={{ mb: 2 }}>
            Le coût d'un accompagnement s'apprécie <strong>au cas par cas</strong> :
            il dépend de l'ampleur et de la complexité de votre dossier. Un
            rendez-vous permet de l'estimer avant tout engagement.
          </Typography>

          <Button variant="contained"
                  onClick={() => ouvrir('ASSISTANCE', 'ASSURANCE')}>
            Demander un rendez-vous
          </Button>
        </Paper>

        {demandes.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 2 }}>Vos demandes</Typography>
            <Stack spacing={2}>
              {demandes.map((d) => (
                <Box key={d.id}>
                  <Stack direction="row" spacing={1} alignItems="baseline"
                         flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined"
                          label={d.type_libelle} />
                    <Chip size="small" variant="outlined"
                          color={d.statut === 'REPONDUE' ? 'success' : 'default'}
                          label={d.statut_libelle} />
                    <Typography variant="caption" color="text.secondary">
                      {dateCourte(d.created_at)}
                    </Typography>
                  </Stack>
                  {d.message && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {d.message}
                    </Typography>
                  )}
                  {d.reponse && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      {d.reponse}
                    </Alert>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        )}
      </Stack>

      <Dialog open={formulaire !== null} onClose={() => setFormulaire(null)}
              fullWidth maxWidth="sm">
        <DialogTitle>
          {formulaire?.type === 'RAPPEL' ? 'Être rappelé'
            : formulaire?.type === 'ASSISTANCE' ? 'Demander un rendez-vous'
            : 'Votre question'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formulaire?.type === 'QUESTION' && (
              <TextField select label="De quoi s'agit-il ?"
                         value={formulaire.sujet}
                         onChange={(e) => setFormulaire((f) => ({
                           ...f, sujet: e.target.value }))}>
                {SUJETS.map((s) => (
                  <MenuItem key={s.code} value={s.code}>{s.libelle}</MenuItem>
                ))}
              </TextField>
            )}

            {formulaire?.type !== 'QUESTION' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Votre téléphone" fullWidth
                           value={formulaire?.telephone ?? ''}
                           onChange={(e) => setFormulaire((f) => ({
                             ...f, telephone: e.target.value }))} />
                <TextField label="Quand vous joindre" fullWidth
                           placeholder="En matinée, après 18 h…"
                           value={formulaire?.moment ?? ''}
                           onChange={(e) => setFormulaire((f) => ({
                             ...f, moment: e.target.value }))} />
              </Stack>
            )}

            <TextField
              label={formulaire?.type === 'ASSISTANCE'
                ? 'Ce qui vous bloque' : 'Votre message'}
              multiline minRows={4}
              value={formulaire?.message ?? ''}
              onChange={(e) => setFormulaire((f) => ({
                ...f, message: e.target.value }))}
              helperText={formulaire?.type === 'ASSISTANCE'
                ? "Dites-nous où vous en êtes : nous préparerons le rendez-vous."
                : "Plus vous êtes précis, plus la réponse sera utile."}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormulaire(null)}>Annuler</Button>
          <Button variant="contained" onClick={envoyer}
                  disabled={occupe || !formulaire?.message?.trim()}>
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
